import { createFileRoute, Link } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { ArrowLeft } from "lucide-react";
import {
  Bar,
  BarChart,
  CartesianGrid,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import { useState } from "react";
import { AppShell } from "@/components/layout/AppShell";
import { BiFilterBar, useBiFilters } from "@/components/bi/BiFilters";
import {
  ChartCard,
  ChartState,
  formatMoneyCLP,
  formatNumber,
  tooltipStyle,
} from "@/components/bi/BiPrimitives";
import { cn } from "@/lib/utils";
import { biAnalyticsService } from "@/services/biAnalytics.service";

export const Route = createFileRoute("/bi/organismos")({
  head: () => ({ meta: [{ title: "Reporte Organismos — LicitaPro" }] }),
  component: Page,
});

function Page() {
  const { filters, setFilters } = useBiFilters();
  const [metric, setMetric] = useState<"count" | "amount">("count");

  const organismos = useQuery({
    queryKey: ["bi", "rankings", "organismo", metric, filters],
    queryFn: () => biAnalyticsService.rankings(filters, "organismo", metric, 15),
  });
  const regiones = useQuery({
    queryKey: ["bi", "breakdown", "region", filters],
    queryFn: () => biAnalyticsService.breakdown(filters, "region"),
  });
  const gastoCreciente = useQuery({
    queryKey: ["bi", "organismos-gasto-creciente", filters],
    queryFn: () => biAnalyticsService.organismosGastoCreciente(filters),
  });

  return (
    <AppShell
      title="Organismos y regiones"
      subtitle="Quién compra y dónde se concentra la demanda"
      actions={
        <Link
          to="/bi"
          className="inline-flex items-center gap-1.5 text-xs px-2.5 py-1.5 rounded-md border border-border hover:bg-muted text-muted-foreground hover:text-foreground transition"
        >
          <ArrowLeft className="size-3.5" /> Reportes
        </Link>
      }
    >
      <BiFilterBar />

      <div className="grid grid-cols-1 xl:grid-cols-2 gap-4">
        <ChartCard
          title="Top organismos compradores"
          subtitle="Ordenado por volumen o monto CLP"
          actions={
            <div className="flex gap-1">
              {(["count", "amount"] as const).map((m) => (
                <button
                  key={m}
                  type="button"
                  onClick={() => setMetric(m)}
                  className={cn(
                    "text-xs px-2 py-1 rounded-md border transition",
                    metric === m
                      ? "border-primary bg-primary/10 text-primary"
                      : "border-border text-muted-foreground hover:bg-muted",
                  )}
                >
                  {m === "count" ? "Volumen" : "Monto"}
                </button>
              ))}
            </div>
          }
        >
          <ChartState
            loading={organismos.isLoading}
            error={organismos.error}
            empty={!organismos.data?.length}
            height={420}
          >
            <ResponsiveContainer width="100%" height={420}>
              <BarChart data={organismos.data} layout="vertical">
                <CartesianGrid strokeDasharray="3 3" stroke="var(--color-border)" horizontal={false} />
                <XAxis
                  type="number"
                  stroke="var(--color-muted-foreground)"
                  fontSize={11}
                  tickLine={false}
                  axisLine={false}
                />
                <YAxis
                  type="category"
                  dataKey="name"
                  stroke="var(--color-muted-foreground)"
                  fontSize={10}
                  tickLine={false}
                  axisLine={false}
                  width={170}
                />
                <Tooltip contentStyle={tooltipStyle} />
                <Bar
                  dataKey={metric === "count" ? "count" : "amount"}
                  name={metric === "count" ? "Licitaciones" : "Monto CLP"}
                  fill="var(--color-chart-1)"
                  radius={[0, 4, 4, 0]}
                />
              </BarChart>
            </ResponsiveContainer>
          </ChartState>
        </ChartCard>

        <ChartCard title="Distribución regional" subtitle="Haz clic en una región para filtrar">
          <ChartState
            loading={regiones.isLoading}
            error={regiones.error}
            empty={!regiones.data?.length}
            height={420}
          >
            <div className="space-y-1.5 max-h-[420px] overflow-y-auto pr-1">
              {(regiones.data ?? []).map((row) => {
                const max = Math.max(1, ...(regiones.data ?? []).map((r) => r.count));
                return (
                  <button
                    key={row.name}
                    type="button"
                    onClick={() => setFilters({ region: row.name })}
                    className="w-full text-left p-2 rounded-lg hover:bg-muted/50 transition"
                  >
                    <div className="flex items-center justify-between text-xs mb-1">
                      <span className="text-foreground truncate">{row.name}</span>
                      <span className="text-muted-foreground tabular-nums shrink-0 ml-2">
                        {formatNumber(row.count)} · {formatMoneyCLP(row.amount)}
                      </span>
                    </div>
                    <div className="h-1.5 rounded-full bg-muted overflow-hidden">
                      <div
                        className="h-full rounded-full bg-primary"
                        style={{ width: `${(row.count / max) * 100}%` }}
                      />
                    </div>
                  </button>
                );
              })}
            </div>
          </ChartState>
        </ChartCard>
      </div>

      <ChartCard
        title="Organismos con gasto creciente en tu rubro"
        subtitle="Compara la ventana actual contra el período previo equivalente, solo en los rubros de tu perfil de empresa"
        className="mt-4"
      >
        <ChartState
          loading={gastoCreciente.isLoading}
          error={gastoCreciente.error}
          empty={!gastoCreciente.data?.length}
          height={160}
        >
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-xs text-muted-foreground border-b border-border">
                  <th className="text-left font-medium py-2">Organismo</th>
                  <th className="text-right font-medium py-2">Período previo</th>
                  <th className="text-right font-medium py-2">Período actual</th>
                  <th className="text-right font-medium py-2">Variación</th>
                </tr>
              </thead>
              <tbody>
                {(gastoCreciente.data ?? []).map((row) => (
                  <tr key={row.organismo} className="border-b border-border/60 last:border-0">
                    <td className="py-2 text-foreground">{row.organismo}</td>
                    <td className="py-2 text-right tabular-nums text-muted-foreground">
                      {formatMoneyCLP(row.montoAnterior)}
                    </td>
                    <td className="py-2 text-right tabular-nums text-foreground">
                      {formatMoneyCLP(row.montoReciente)}
                    </td>
                    <td className="py-2 text-right tabular-nums font-medium text-success">
                      {row.variacionPct != null ? `+${row.variacionPct}%` : "Nuevo"}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </ChartState>
      </ChartCard>
    </AppShell>
  );
}
